import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { serviceLabel } from "@/lib/service";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Landmark, Clock, Gift, AlertTriangle, Eye, EyeOff } from "lucide-react";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user, register, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [overnight, setOvernight] = useState(false);
  const { data: faqCfg } = useQuery({ queryKey: ["public-faq"], queryFn: async () => (await api.get("/public/faq")).data, staleTime: 60000 });
  const rushOpen = faqCfg ? !!faqCfg.rush_available : true;
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [applying, setApplying] = useState(false);
  const [firstOrder, setFirstOrder] = useState(false);
  const discount = coupon?.discount || 0;
  const DELIVERY_FEE = 5;
  const OVERNIGHT_FEE = 10;
  const total = Math.max(0, subtotal - discount) + (delivery ? DELIVERY_FEE : 0) + (overnight ? OVERNIGHT_FEE : 0);
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const { data: rules } = useQuery({ queryKey: ["deposit-rules"], queryFn: async () => (await api.get("/deposit-rules")).data, staleTime: 60000 });
  const ratePct = rules?.rate_pct ?? 50;
  const sheetSqin = rules?.sheet_sqin ?? 408;
  const merchLimit = rules?.merch_qty ?? 10;
  const hasGangSheet = items.some((i) => i.kind === "gangsheet");
  const hasLargeSheet = items.some((i) => i.kind === "gangsheet" && (Number(i.sheet_width) || 0) * (Number(i.sheet_height) || 0) > sheetSqin);
  const merchQty = items.filter((i) => i.kind !== "gangsheet").reduce((s, i) => s + i.quantity, 0);
  const requiresDeposit = hasLargeSheet || merchQty > merchLimit;
  const depositAmount = requiresDeposit ? Math.round(total * ratePct) / 100 : 0;
  const balanceDue = requiresDeposit ? Math.round((total - depositAmount) * 100) / 100 : 0;
  const dueNow = requiresDeposit ? depositAmount : total;
  const minFulfillDate = new Date(Date.now() + (overnight ? 1 : 2) * 86400000).toISOString().slice(0, 10);
  const [fulfillmentDate, setFulfillmentDate] = useState("");
  useEffect(() => {
    if (fulfillmentDate && fulfillmentDate < minFulfillDate) setFulfillmentDate("");
  }, [fulfillmentDate, minFulfillDate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), subtotal });
      setCoupon(data);
      toast.success(`Code ${data.code} applied`);
    } catch (err) {
      setCoupon(null);
      toast.error(apiError(err.response?.data?.detail) || "Invalid code");
    } finally { setApplying(false); }
  };

  const checkFirstOrder = useCallback(async (email) => {
    try {
      const { data } = await api.get("/coupons/first-order", { params: email ? { email } : {} });
      if (!data.eligible) { setFirstOrder(false); return; }
      setFirstOrder(true);
      setCoupon((prev) => prev || null);
      const res = await api.post("/coupons/validate", { code: data.code, subtotal });
      setCoupon((prev) => prev ? prev : res.data);
      setCouponInput((prev) => prev || data.code);
    } catch { /* no offer */ }
  }, [subtotal]);

  useEffect(() => {
    if (user) { setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" })); checkFirstOrder(user.email); }
  }, [user, checkFirstOrder]);

  useEffect(() => { if (items.length === 0) navigate("/catalog"); }, [items.length, navigate]);

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!user) {
        const reg = await register({ name: form.name, email: form.email, phone: form.phone, password });
        if (!reg.ok) {
          const li = await login(form.email, password);
          if (!li.ok) {
            toast.error("An account with this email already exists. Enter its password to continue, or sign in.");
            setLoading(false);
            return;
          }
        }
      }
      const orderPayload = {
        customer: form,
        items: items.map(({ id, ...rest }) => rest),
        coupon_code: coupon?.code || null,
        fulfillment_date: fulfillmentDate,
        delivery,
        overnight,
      };
      const { data: order } = await api.post("/orders", orderPayload);
      navigate(`/order-placed?order_id=${order.order_id}&token=${order.access_token}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not place order");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-3xl font-extrabold text-white">Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-8 mt-8">
        <form onSubmit={pay} className="lg:col-span-3 bg-[#12141a] border border-zinc-800 rounded-2xl p-6 space-y-4" data-testid="checkout-form">
          <h2 className="font-display text-lg font-bold text-white">Customer details</h2>
          <div><Label className="text-zinc-300">Full name</Label><Input data-testid="customer-name-input" required value={form.name} onChange={set("name")} className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13]" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-zinc-300">Email</Label><Input data-testid="customer-email-input" type="email" required value={form.email} onChange={set("email")} onBlur={(e) => { if (!user && /.+@.+\..+/.test(e.target.value)) checkFirstOrder(e.target.value); }} className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13]" /></div>
            <div><Label className="text-zinc-300">Phone</Label><Input data-testid="customer-phone-input" required value={form.phone} onChange={set("phone")} className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13]" /></div>
          </div>
          {!user && (
            <div data-testid="express-account" className="rounded-xl border border-[#ff6a13]/30 bg-[#ff6a13]/5 p-4">
              <p className="text-sm font-semibold text-white flex items-center gap-2"><Lock size={14} className="text-[#ff6a13]" />Express checkout — we'll set up your account</p>
              <p className="text-xs text-zinc-400 mt-1">Choose a password and we'll create your account as you order, so you can track it and reorder later. No extra steps.</p>
              <Label className="text-zinc-300 mt-3 block">Create a password</Label>
              <div className="relative mt-1.5">
                <Input data-testid="checkout-password-input" type={showPw ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="bg-[#1e222d] border-zinc-700 text-white pr-11 focus:border-[#ff6a13] focus:ring-[#ff6a13]" />
                <button type="button" onClick={() => setShowPw((s) => !s)} aria-label="Toggle password" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">Already have an account? <Link to="/login?next=/checkout" className="text-[#ff6a13] hover:underline" data-testid="checkout-signin-link">Sign in</Link> — or just enter your password above and we'll log you in.</p>
            </div>
          )}
          <div>
            <Label className="text-zinc-300">Delivery</Label>
            <label data-testid="delivery-option" className="mt-1.5 flex items-center justify-between gap-3 cursor-pointer rounded-xl border border-zinc-700 bg-[#1e222d] px-4 py-3 hover:border-[#ff6a13] transition-colors">
              <span className="flex items-center gap-3">
                <input type="checkbox" data-testid="delivery-checkbox" checked={delivery} onChange={(e) => setDelivery(e.target.checked)} className="w-4 h-4 accent-[#ff6a13]" />
                <span className="text-sm text-white">Local delivery in Bermuda</span>
              </span>
              <span className="font-mono-p text-[#10b981] text-sm">+$5.00</span>
            </label>
            <p className="text-xs text-zinc-500 mt-1.5">Local pickup is free. Choose delivery or an overnight rush below.</p>
            <label data-testid="overnight-option" className={`mt-2 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${rushOpen ? "cursor-pointer border-zinc-700 bg-[#1e222d] hover:border-[#ff6a13]" : "cursor-not-allowed border-zinc-800 bg-[#15181f] opacity-70"}`}>
              <span className="flex items-center gap-3">
                <input type="checkbox" data-testid="overnight-checkbox" disabled={!rushOpen} checked={overnight && rushOpen} onChange={(e) => setOvernight(e.target.checked)} className="w-4 h-4 accent-[#ff6a13]" />
                <span className="text-sm text-white">Overnight rush order{!rushOpen && <span className="block text-xs text-zinc-500" data-testid="rush-closed-note">Not available this week — we're at capacity</span>}</span>
              </span>
              <span className="font-mono-p text-[#ff007a] text-sm">+$10.00</span>
            </label>
          </div>
          <div>
            <Label className="text-zinc-300">Requested fulfillment date <span className="text-[#ff6a13] font-normal">(required · min {overnight ? "1 day" : "2 days"} out)</span></Label>
            <Input data-testid="fulfillment-date-input" type="date" required min={minFulfillDate} value={fulfillmentDate} onChange={(e) => setFulfillmentDate(e.target.value)} className="mt-1.5 bg-[#1e222d] border-zinc-700 text-white focus:border-[#ff6a13] focus:ring-[#ff6a13]" />
            <p className="text-xs text-zinc-500 mt-1.5">{overnight ? "Rush selected — next-day fulfillment is available. " : ""}Our team will review and confirm this date before starting production.</p>
          </div>
          <Button type="submit" disabled={loading} data-testid="pay-now-btn" className="w-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-12 font-semibold active:scale-95 transition-transform">
            <Landmark size={18} className="mr-2" />{loading ? "Placing order…" : `Place Order — $${dueNow.toFixed(2)}${requiresDeposit ? " deposit" : ""}`}
          </Button>
          <p className="text-xs text-zinc-500 flex items-center gap-1.5 justify-center"><Lock size={12} />Pay by direct bank deposit — instructions on the next step</p>
        </form>

        <div className="lg:col-span-2 bg-[#12141a] border border-zinc-800 rounded-2xl p-6 h-fit">
          <h2 className="font-display text-lg font-bold text-white">Order summary</h2>
          <div className="space-y-3 mt-4">
            {items.some((i) => i.kind === "gangsheet" && i.low_dpi) && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2.5 flex items-start gap-2" data-testid="checkout-lowdpi-warning">
                <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-300 leading-snug">Some gang sheet designs are below 150 DPI and may print blurry. You can go back to the builder to resize them, or continue if you're happy with the quality.</p>
              </div>
            )}
            {items.map((it) => (
              <div key={it.id} className="flex gap-3">
                <div className="w-12 h-12 checker-light rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                  {(it.image_url || it.preview_url) && <img src={fileUrl(it.preview_url || it.image_url)} alt="" className="max-w-full max-h-full object-contain" />}
                </div>
                <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{it.name}</p><p className="text-xs text-zinc-500 font-mono-p">Qty {it.quantity}{serviceLabel(it) ? ` · ${serviceLabel(it)}` : ""}</p></div>
                <span className="text-sm font-mono-p text-zinc-300">${(it.unit_price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-zinc-800">
            {firstOrder && (
              <div className="mb-3 rounded-xl border border-[#10b981]/40 bg-[#10b981]/10 px-3 py-2.5 flex items-center gap-2" data-testid="first-order-banner">
                <Gift size={16} className="text-[#10b981] shrink-0" />
                <p className="text-xs text-[#10b981] leading-snug"><span className="font-semibold">First order?</span> WELCOME5 has been applied for you — enjoy the discount!</p>
              </div>
            )}
            <Label className="text-zinc-300 text-xs">Promo code</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="e.g. WELCOME10" className="bg-[#1e222d] border-zinc-700 text-white font-mono-p uppercase focus:border-[#ff6a13] focus:ring-[#ff6a13]" />
              <Button type="button" onClick={applyCoupon} disabled={applying} data-testid="apply-coupon-btn" variant="outline" className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800 shrink-0">{applying ? "…" : "Apply"}</Button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="one-code-note">Only one discount code per order.</p>
          </div>
          <div className="mt-4 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Subtotal</span><span className="font-mono-p text-zinc-300">${subtotal.toFixed(2)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-sm" data-testid="checkout-discount"><span className="text-[#10b981]">Discount ({coupon.code})</span><span className="font-mono-p text-[#10b981]">−${discount.toFixed(2)}</span></div>
            )}
            {delivery && (
              <div className="flex justify-between text-sm" data-testid="checkout-delivery"><span className="text-zinc-400">Local delivery</span><span className="font-mono-p text-zinc-300">+$5.00</span></div>
            )}
            {overnight && (
              <div className="flex justify-between text-sm" data-testid="checkout-overnight"><span className="text-zinc-400">Overnight rush</span><span className="font-mono-p text-zinc-300">+$10.00</span></div>
            )}
            <div className="flex justify-between pt-2 border-t border-zinc-800"><span className="text-zinc-400">Total</span><span className="font-bold text-lg font-mono-p text-white" data-testid="checkout-total">${total.toFixed(2)}</span></div>
          </div>
          {requiresDeposit && (
            <div className="mt-4 rounded-xl border border-[#ffd600]/30 bg-[#ffd600]/10 p-3 space-y-1.5" data-testid="deposit-notice">
              <p className="text-xs text-[#ffd600] font-semibold uppercase tracking-wider font-mono-p">{ratePct}% Deposit Required</p>
              {hasLargeSheet ? (
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="gangsheet-deposit-note">{ratePct}% deposit required for larger gang sheets. Balance due before collection.</p>
              ) : (
                <p className="text-xs text-zinc-300 leading-relaxed">Merch orders over {merchLimit} items require a {ratePct}% deposit. The balance and delivery date will be agreed upon after your order is placed.</p>
              )}
              <div className="flex justify-between text-sm pt-1"><span className="text-zinc-400">Deposit due now ({ratePct}%)</span><span className="font-bold font-mono-p text-white" data-testid="deposit-due">${depositAmount.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-400">{hasLargeSheet ? "Balance due before collection" : "Balance due later"}</span><span className="font-mono-p text-zinc-300" data-testid="deposit-balance">${balanceDue.toFixed(2)}</span></div>
            </div>
          )}
          {!requiresDeposit && (
            <div className="mt-4 flex justify-between text-sm rounded-xl border border-[#10b981]/30 bg-[#10b981]/10 p-3" data-testid="full-payment-notice"><span className="text-zinc-300">Total amount due now</span><span className="font-bold font-mono-p text-white" data-testid="due-now">${total.toFixed(2)}</span></div>
          )}
          <div className="mt-4 flex items-start gap-2 text-xs text-[#22c3d6] bg-[#22c3d6]/10 border border-[#22c3d6]/30 rounded-xl px-3 py-2 font-mono-p" data-testid="turnaround-badge"><Clock size={13} className="mt-0.5 shrink-0" /> <span>Production starts once your deposit is received &amp; your requested fulfillment date is approved</span></div>
        </div>
      </div>
    </div>
  );
}
